import React from "react";

/**
 * The reading format of a pathway material (contract §11.5): plain text with a small
 * Markdown subset, turned into React ELEMENTS. No HTML is ever interpreted: every piece of
 * the author's text reaches the page as a React text child, which React escapes, so a
 * `<script>` or an `<img onerror=…>` in a reading is shown as the characters it is.
 *
 *   Blocks   paragraphs, separated by a blank line (a single line break inside a
 *            paragraph is kept as a line break: authors write in a plain textarea)
 *            "# ", "## ", "### " headings  -> <h3>, <h4>, <h5> (the page around a
 *                                           reading already owns <h1> and <h2>)
 *            "- ", "* " or "+ " bullet lists, "1. " numbered lists (consecutive lines
 *                                           form one list; a marker with nothing after it,
 *                                           as text pasted from chat tools often ends a list
 *                                           with, adds no empty item)
 *            "---", "***" or "___" on a line of its own (3 or more, spaces allowed
 *                                           between)                  -> a divider <hr>
 *            "> " quotes (consecutive lines form one quote)
 *            ``` fenced code blocks; an unterminated fence runs to the end of the text
 *   Inline   **bold**, *italic* (nestable either way), `code`, [text](https://…)
 *
 * Links: a link renders only for an http(s) target with a host, opens in a new tab with
 * `rel="noopener noreferrer"`, and otherwise falls back to its text (`javascript:`,
 * `data:`, a relative path, … keep the words and lose the target).
 *
 * Emphasis follows the usual flanking rule (an opener is followed by a non-space, a
 * closer preceded by one), so "2 * 3 * 4" stays arithmetic. An unmatched delimiter is
 * literal text. Emphasis nests at most MAX_DEPTH levels; anything deeper is text.
 *
 * `parseReading` is the pure half (text -> block tree), `ReadingMarkup` the renderer; the
 * builder's Preview and the learner's player both render through `ReadingMarkup`, so the
 * two can never show a reading differently.
 */

const MAX_DEPTH = 8;

const FENCE = /^ {0,3}```/;
const FENCE_CLOSE = /^ {0,3}```\s*$/;
const BLANK = /^\s*$/;
const HEADING = /^ {0,3}(#{1,3})[ \t]+(.*\S)\s*$/;
// A divider is checked BEFORE a bullet: "* * *" is a divider, not a list of stars.
const RULE = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/;
const BULLET = /^ {0,3}[-*+](?:[ \t]+(.*))?$/;
const NUMBERED = /^ {0,3}(\d{1,9})\.[ \t]+(.*)$/;
const QUOTE = /^ {0,3}>[ \t]?(.*)$/;
// "[text](target)"; the target may hold one level of balanced parentheses (Wikipedia
// URLs, and `javascript:alert(1)`, which must still be RECOGNISED to be neutralised).
const LINK = /\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/y;
const SPACE = /\s/;

const startsBlock = (line) => FENCE.test(line) || HEADING.test(line) || RULE.test(line) || BULLET.test(line)
  || NUMBERED.test(line) || QUOTE.test(line);

/**
 * The block tree of a reading body.
 *
 * @param {string} body
 * @returns {Array<
 *   { type: "paragraph", lines: string[] } |
 *   { type: "heading", level: 1|2|3, text: string } |
 *   { type: "rule" } |
 *   { type: "bullets", items: string[] } |
 *   { type: "numbered", start: number, items: string[] } |
 *   { type: "quote", lines: string[] } |
 *   { type: "code", text: string }
 * >}
 */
export function parseReading(body) {
  const lines = String(body ?? "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (BLANK.test(line)) {
      i += 1;
    } else if (FENCE.test(line)) {
      const code = [];
      i += 1;
      while (i < lines.length && !FENCE_CLOSE.test(lines[i])) { code.push(lines[i]); i += 1; }
      i += 1; // past the closing fence (or the end of an unterminated one)
      blocks.push({ type: "code", text: code.join("\n") });
    } else if (HEADING.test(line)) {
      const [, hashes, text] = HEADING.exec(line);
      blocks.push({ type: "heading", level: hashes.length, text });
      i += 1;
    } else if (RULE.test(line)) {
      blocks.push({ type: "rule" });
      i += 1;
    } else if (BULLET.test(line)) {
      const items = [];
      while (i < lines.length && !RULE.test(lines[i]) && BULLET.test(lines[i])) {
        const item = (BULLET.exec(lines[i])[1] ?? "").trim();
        if (item) items.push(item);
        i += 1;
      }
      if (items.length) blocks.push({ type: "bullets", items });
    } else if (NUMBERED.test(line)) {
      const start = Number(NUMBERED.exec(line)[1]);
      const items = [];
      while (i < lines.length && NUMBERED.test(lines[i])) { items.push(NUMBERED.exec(lines[i])[2]); i += 1; }
      blocks.push({ type: "numbered", start, items });
    } else if (QUOTE.test(line)) {
      const quoted = [];
      while (i < lines.length && QUOTE.test(lines[i])) { quoted.push(QUOTE.exec(lines[i])[1]); i += 1; }
      blocks.push({ type: "quote", lines: quoted });
    } else {
      const para = [];
      while (i < lines.length && !BLANK.test(lines[i]) && (para.length === 0 || !startsBlock(lines[i]))) {
        para.push(lines[i].trim());
        i += 1;
      }
      blocks.push({ type: "paragraph", lines: para });
    }
  }
  return blocks;
}

// ── inline ───────────────────────────────────────────────────────────────────
//
// Two passes, the way CommonMark resolves emphasis. TOKENIZE left to right: code spans and
// links are resolved on the spot (a star inside either is never a delimiter), and every
// run of stars becomes a delimiter token that may open (followed by a non-space) and may
// close (preceded by one). Then MATCH: each closing run takes the nearest opening run to
// its left; two stars on both sides make a strong, otherwise one star makes an italic, and
// the tokens between them become its children. What a run has left over is literal text.
// A closer walks left to its opener, and openers that can no longer match are cut off
// (see `floor`), so a long hostile body does not go quadratic in its nesting.

/** The end of the code span opened by the backtick at `at`, or -1 (an empty span is not one). */
const codeSpanEnd = (src, at) => {
  const end = src.indexOf("`", at + 1);
  return end > at + 1 ? end : -1;
};

/** An http(s) URL with a host, as the browser will read it; "" for anything else. */
function safeHref(raw) {
  if (!/^https?:\/\//i.test(raw)) return "";
  let parsed;
  try {
    parsed = new URL(raw);
  } catch {
    return "";
  }
  return /^https?:$/.test(parsed.protocol) && parsed.hostname ? parsed.href : "";
}

const depthOf = (node) => node.depth || 0;
const maxDepth = (nodes) => nodes.reduce((d, n) => Math.max(d, depthOf(n)), 0);

/**
 * Settled tokens as render nodes: a star run nothing matched is literal text, and
 * neighbouring text merges (a hostile body stays a handful of text nodes, not thousands).
 */
function settle(tokens) {
  const out = [];
  for (const tok of tokens) {
    const value = tok.type === "delim" ? "*".repeat(tok.count) : tok.type === "text" ? tok.value : null;
    if (value === null) out.push(tok);
    else if (!value) continue;
    else if (out.length && out[out.length - 1].type === "text") out[out.length - 1] = { type: "text", value: out[out.length - 1].value + value };
    else out.push({ type: "text", value });
  }
  return out;
}

/** Text, code, link and star-run tokens of one run of text. */
function tokenize(src, links) {
  const tokens = [];
  let text = "";
  const flush = () => {
    if (text) tokens.push({ type: "text", value: text });
    text = "";
  };
  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    if (ch === "`") {
      const end = codeSpanEnd(src, i);
      if (end > 0) {
        flush();
        tokens.push({ type: "code", value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    } else if (ch === "*") {
      let j = i;
      while (src[j] === "*") j += 1;
      const before = src[i - 1];
      const after = src[j];
      flush();
      tokens.push({
        type: "delim",
        count: j - i,
        canOpen: after !== undefined && !SPACE.test(after),
        canClose: before !== undefined && !SPACE.test(before),
      });
      i = j;
      continue;
    } else if (ch === "[" && links) {
      LINK.lastIndex = i;
      const m = LINK.exec(src);
      if (m) {
        flush();
        const href = safeHref(m[2]);
        const children = parseInline(m[1], false);
        tokens.push({ type: href ? "link" : "plain", href, children, depth: 1 + maxDepth(children) });
        i += m[0].length;
        continue;
      }
    }
    text += ch;
    i += 1;
  }
  flush();
  return tokens;
}

/**
 * The inline tree of one run of text.
 *
 * @param {string} src
 * @param {boolean} links  false inside a link's own text (a link never nests)
 * @returns {Array<object>}
 */
function parseInline(src, links) {
  const tokens = tokenize(src, links);
  // Openers at or left of `floor` are dead: they could only wrap a range that already
  // exceeds the nesting cap. Nothing is ever spliced left of it, so the index holds.
  let floor = -1;
  let i = 0;
  while (i < tokens.length) {
    const closer = tokens[i];
    if (closer.type === "delim" && closer.canClose) {
      let o = i - 1;
      while (closer.count > 0) {
        while (o > floor && !(tokens[o].type === "delim" && tokens[o].canOpen && tokens[o].count > 0)) o -= 1;
        if (o <= floor) break;
        const opener = tokens[o];
        let depth = 1;
        for (let k = o + 1; k < i; k += 1) depth = Math.max(depth, 1 + depthOf(tokens[k]));
        // Deeper than the cap: this closer stays text, and so does every opener from here
        // leftwards (any later closer reaching one of them would wrap even more).
        if (depth > MAX_DEPTH) {
          floor = o;
          break;
        }
        const use = opener.count >= 2 && closer.count >= 2 ? 2 : 1;
        opener.count -= use;
        closer.count -= use;
        const inner = tokens.splice(o + 1, i - o - 1, null);
        tokens[o + 1] = { type: use === 2 ? "strong" : "em", children: settle(inner), depth };
        i = o + 2;
      }
    }
    i += 1;
  }
  return settle(tokens);
}

// ── render ───────────────────────────────────────────────────────────────────

function renderInline(nodes) {
  return nodes.map((n, i) => {
    switch (n.type) {
      case "text": return <React.Fragment key={i}>{n.value}</React.Fragment>;
      case "code": return <code key={i} className="rounded bg-eh-cream px-1 py-0.5 text-[0.9em] font-mono">{n.value}</code>;
      case "strong": return <strong key={i} className="font-semibold">{renderInline(n.children)}</strong>;
      case "em": return <em key={i}>{renderInline(n.children)}</em>;
      case "link":
        return (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer" className="text-eh-sage-deep underline underline-offset-2 hover:text-eh-ink">
            {renderInline(n.children)}
          </a>
        );
      default: return <React.Fragment key={i}>{renderInline(n.children)}</React.Fragment>;
    }
  });
}

const inline = (s) => renderInline(parseInline(s, true));

/** Lines of one paragraph or quote, their single line breaks kept. */
const withBreaks = (lines) => lines.map((line, i) => (
  <React.Fragment key={i}>
    {i > 0 && <br />}
    {inline(line)}
  </React.Fragment>
));

const HEADING_TAGS = { 1: "h3", 2: "h4", 3: "h5" };
const HEADING_CLASSES = {
  1: "font-display text-2xl text-eh-ink mt-6 mb-2",
  2: "font-display text-xl text-eh-ink mt-5 mb-2",
  3: "text-base font-semibold text-eh-ink mt-4 mb-1.5",
};

function renderBlock(block, i) {
  switch (block.type) {
    case "heading": {
      const Tag = HEADING_TAGS[block.level];
      return <Tag key={i} className={HEADING_CLASSES[block.level]}>{inline(block.text)}</Tag>;
    }
    case "rule":
      return <hr key={i} className="my-5 border-eh-border-soft" />;
    case "bullets":
      return (
        <ul key={i} className="list-disc pl-6 space-y-1 my-3">
          {block.items.map((item, j) => <li key={j}>{inline(item)}</li>)}
        </ul>
      );
    case "numbered":
      return (
        <ol key={i} start={block.start === 1 ? undefined : block.start} className="list-decimal pl-6 space-y-1 my-3">
          {block.items.map((item, j) => <li key={j}>{inline(item)}</li>)}
        </ol>
      );
    case "quote":
      return (
        <blockquote key={i} className="border-l-4 border-eh-sage-soft pl-4 my-3 italic text-eh-ink/80">
          {withBreaks(block.lines)}
        </blockquote>
      );
    case "code":
      return (
        <pre key={i} className="my-3 overflow-x-auto rounded-xl bg-eh-ink text-eh-cream p-4 text-sm leading-relaxed">
          <code className="font-mono">{block.text}</code>
        </pre>
      );
    default:
      return <p key={i} className="my-3 break-words">{withBreaks(block.lines)}</p>;
  }
}

/**
 * A reading body, rendered.
 *
 * @param {object} props
 * @param {string} props.body       Markdown-lite text (contract §11.5)
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
export default function ReadingMarkup({ body, className = "", testId }) {
  return (
    <div data-testid={testId} className={`text-eh-ink leading-relaxed ${className}`}>
      {parseReading(body).map(renderBlock)}
    </div>
  );
}
